import type { Georole, Person } from '@domain/person';
import type { StructureBase } from '@lib/structures';
import type { FunctionComponent } from 'react';
import {
  EuiBadge,
  EuiButtonIcon,
  EuiFlexGroup,
  EuiFlexItem,
  EuiPanel,
  EuiText,
  EuiToolTip,
} from '@elastic/eui';
import { css } from '@emotion/react';
import RemoveGeorole from './remove-georole';
import AddGeorole from './add-georole';
import { structConf } from '@lib/structures';

interface GeorolePanelProps {
  person: Person;
  georole: Georole;
}

const badgeName = (s: StructureBase): string => {
  if (s.type === 'country') {
    return 'South Africa';
  } else if (s.type === 'region') {
    return `${s.name} (region)`;
  } else if (s.type === 'constituency') {
    return `${s.name} (const.)`;
  } else if (s.type === 'municipality') {
    return `${s.name} (muni.)`;
  } else if (s.type === 'votingdistrict') {
    return `${s.name} (VD)`;
  } else {
    return s.name;
  }
};

const GeorolePanel: FunctionComponent<GeorolePanelProps> = props => {
  const { georole, person } = props;
  const { roleName, structures } = georole;

  return (
    <EuiPanel paddingSize="s">
      <EuiFlexGroup direction="row" gutterSize="none" responsive={false}>
        <EuiFlexItem>
          <EuiText textAlign="left" size="s">
            <p>
              <span
                css={css`
                  font-weight: bold;
                `}>
                {roleName}
              </span>
            </p>
          </EuiText>
        </EuiFlexItem>
        {!person.authn?.suspended && (
          <EuiFlexItem grow={false}>
            <AddGeorole
              person={person}
              roleName={roleName}
              button={<EuiButtonIcon iconType="plus" color="primary" />}
              tooltip={`Add ${roleName} permisisons`}
            />
          </EuiFlexItem>
        )}
        <EuiFlexItem grow={false}>
          <EuiToolTip content={`Remove all ${roleName} permissions`}>
            <RemoveGeorole
              person={person}
              roleName={roleName}
              structures={structures}
              button={
                <EuiButtonIcon
                  iconType="cross"
                  color="danger"
                  aria-label={`Remove all ${roleName} permissions`}
                />
              }
            />
          </EuiToolTip>
        </EuiFlexItem>
      </EuiFlexGroup>
      <EuiFlexGroup direction="row" gutterSize="s" responsive={false} wrap>
        {structures.map(s => (
          <EuiFlexItem key={s.code} grow={false}>
            <EuiBadge color={structConf[s.type].color}>
              {badgeName(s)}{' '}
              <EuiToolTip
                content={`Remove ${roleName} permission for ${badgeName(s)}`}>
                <RemoveGeorole
                  person={person}
                  roleName={roleName}
                  structures={[s]}
                  button={
                    <EuiButtonIcon
                      iconType="cross"
                      iconSize="s"
                      color="danger"
                      aria-label={`Remove ${roleName} permission for ${badgeName(s)}`}
                    />
                  }
                />
              </EuiToolTip>
            </EuiBadge>
          </EuiFlexItem>
        ))}
      </EuiFlexGroup>
    </EuiPanel>
  );
};

export default GeorolePanel;
